export type ErrorData = {
  message?: string
  stack?: string
  [key: string]: unknown
}

export type AggregatedError = {
  errorData: ErrorData
  count: number
  firstSeen: number // timestamp when first aggregated
  lastSeen: number // timestamp when last aggregated
}

/**
 * Build a signature so we can identify duplicates.
 */
const buildSignature = (err: ErrorData): string => {
  const msg = err.message || 'unknown'
  const st = err.stack || 'nostack'
  return `${msg}::${st}`
}

/**
 * A lightweight in-memory aggregator for errors,
 * Groups identical errors increasing `count` until queue is flushed.
 */
export const createErrorAggregator = () => {
  const store = new Map<string, AggregatedError>()

  /**
   * Add/update an error in the aggregator.
   * If it already exists, increment count. Otherwise, create a new entry.
   */
  const aggregateError = (errorData: ErrorData) => {
    const signature = buildSignature(errorData)
    const existing = store.get(signature)
    const now = Date.now()

    if (!existing) {
      store.set(signature, {
        errorData,
        count: 1,
        firstSeen: now,
        lastSeen: now,
      })
    } else {
      existing.count++
      existing.lastSeen = now
      store.set(signature, existing)
    }
  }

  /**
   * Get all errors currently in the aggregator as [signature, aggregatedError].
   */
  const getAll = (): [string, AggregatedError][] => {
    return Array.from(store.entries())
  }

  /**
   * Clear all or just specific signatures
   */
  const clear = (signatures?: string[]) => {
    if (!signatures) {
      store.clear()
      return
    }
    for (const sig of signatures) {
      store.delete(sig)
    }
  }

  /**
   * Clear errors older than the specified age (in milliseconds) to prevent memory growth
   */
  const clearOldErrors = (maxAgeMs: number) => {
    const cutoff = Date.now() - maxAgeMs
    let count = 0
    for (const [signature, agg] of store.entries()) {
      if (agg.lastSeen < cutoff) {
        count = count + 1
        store.delete(signature)
      }
    }
    return count
  }

  return {
    aggregateError,
    getAll,
    clear,
    clearOldErrors,
  }
}

export type ErrorAggregator = ReturnType<typeof createErrorAggregator>
